import { DiscussaoResponseStatus, PagamentoResponseStatus } from '@/api/generated/model';

/**
 * Rótulos e cores da discussão técnica.
 *
 * RASCUNHO é o único status que nunca aparece pro advisor — a discussão só
 * existe pra ele a partir de ABERTA (pagamento confirmado). Aqui é só rótulo
 * de exibição; a regra de quem vê o quê é do backend.
 */
export const STATUS = Object.values(DiscussaoResponseStatus);

export const ROTULO_STATUS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  ABERTA: 'Aberta',
  EM_ATENDIMENTO: 'Em atendimento',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};

/**
 * RASCUNHO cobre dois momentos bem diferentes desde que o rascunho do wizard
 * web nasce incremental (na transição "Assunto" → "Exames a compartilhar",
 * bem antes do advisor escolhido — ver `AberturaDiscussaoWizard`): ainda
 * sendo preenchido, ou já completo e só faltando o pagamento. `ROTULO_STATUS`
 * sozinho não distingue os dois; esta função decide pelo mesmo sinal que o
 * resto do front já usa para "advisor ainda não escolhido" — `assessorId`
 * nulo. Use esta função sempre que tiver a discussão inteira em mãos (linha
 * de tabela, detalhe); `ROTULO_STATUS` sozinho continua certo para popular um
 * filtro por status, que só tem a chave, não a linha.
 */
export function rotuloDaDiscussao(discussao: { status: string; assessorId: string | null }): string {
  if (discussao.status === 'RASCUNHO') {
    return discussao.assessorId ? 'Aguardando pagamento' : 'Em preenchimento';
  }
  return ROTULO_STATUS[discussao.status] ?? discussao.status;
}

export const CLASSE_STATUS: Record<string, string> = {
  RASCUNHO: 'text-amber-600 border-amber-600/40',
  ABERTA: 'text-brand border-brand/40',
  EM_ATENDIMENTO: 'text-emerald-600 border-emerald-600/40',
  ENCERRADA: '',
  CANCELADA: 'text-destructive border-destructive/40',
};

/**
 * Par de `rotuloDaDiscussao`: o laranja de `CLASSE_STATUS.RASCUNHO` é o aviso
 * de "aguardando pagamento" — não deveria acender pra quem ainda nem chegou
 * lá. "Em preenchimento" usa o outline neutro, mesma cor que `ENCERRADA` já
 * usa pra "sem alerta nenhum" (é o padrão do resto do app pra RASCUNHO, ver
 * `features/receituarios/rotulos.ts` e `features/requisicoes/rotulos.ts`).
 */
export function classeDaDiscussao(discussao: { status: string; assessorId: string | null }): string {
  if (discussao.status === 'RASCUNHO' && !discussao.assessorId) return '';
  return CLASSE_STATUS[discussao.status] ?? '';
}

export const ROTULO_PAGAMENTO_STATUS: Record<string, string> = {
  PENDENTE: 'Aguardando confirmação',
  PAGO: 'Pago',
  EXPIRADO: 'Expirado',
  ESTORNO_PENDENTE: 'Estornando',
  ESTORNADO: 'Estornado',
  FALHOU: 'Falhou',
};

export const STATUS_PAGAMENTO = Object.values(PagamentoResponseStatus);

/** Centavos → `R$ 69,90`. */
export function formatarCentavos(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Dias até o fechamento automático por inatividade (5 dias sem resposta desde
 * a 1a mensagem, ou 48h sem o advisor responder). `null` sem prazo correndo —
 * é o que `prazoAutoEncerramento` já significa no backend.
 */
export function diasParaEncerramentoAutomatico(
  prazoAutoEncerramento: string | null,
): number | null {
  if (!prazoAutoEncerramento) return null;
  const ms = new Date(prazoAutoEncerramento).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

/** "Hoje" / "1 dia" / "3 dias" — para colunas e espaços curtos. */
export function rotuloDiasParaEncerrar(dias: number): string {
  if (dias === 0) return 'Hoje';
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/** "Encerra hoje" / "Encerra em 1 dia" / "Encerra em 3 dias" — frase completa. */
export function rotuloPrazoEncerramento(dias: number): string {
  if (dias === 0) return 'Encerra hoje';
  return `Encerra em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/** Fica mais chamativo perto do fechamento — 1 dia é "vai fechar amanhã". */
export function classeUrgenciaPrazo(dias: number): string {
  if (dias <= 1) return 'text-destructive';
  if (dias <= 2) return 'text-amber-600';
  return 'text-muted-foreground';
}
